import { useMemo, useState } from "react";
import { lapso } from "../services/lapso";
import { useTranslation } from "react-i18next";

import Modal from "../../../components/base/Modal";
import { Badge, Button, Spinner } from "../../../components/ui";
import { useCerrarCaso } from "../../../features/centinela/mutations";
import { useCentinelaCases } from "../../../features/centinela/queries";
import {
  mapCentinelaCases,
  type CentinelaCaseView,
} from "../services/mapCentinelaCase";
import { CASE_URGENCY_TONES } from "../theme";

/**
 * Los casos abiertos de toda la flota, en un solo lugar donde se leen y se
 * cierran.
 *
 * ── Por qué existe ──────────────────────────────────────────────────────────
 * Centinela decía «necesita que alguien lo vea» en el resumen y no había por
 * dónde ir a verlo: había que cambiar de sección, encontrar el caso en una
 * lista y recordar de cuál hablaba el saludo. Un aviso sin salida se acaba
 * leyendo como decoración.
 *
 * Y cerrar un caso vivía sólo dentro del Centro de monitoreo, de uno en uno,
 * seleccionando la unidad primero. Cuando hay cinco casos abiertos, eso son
 * cinco viajes por la misma pantalla.
 *
 * ── La nota sigue siendo obligatoria ────────────────────────────────────────
 * Aquí es más importante que en ningún lado. Una lista con un botón de cerrar
 * por renglón invita a vaciarla sin leer, y un caso cerrado AFIRMA que algo ya
 * no requiere atención: si después resulta que sí la requería, la pregunta va a
 * ser quién lo dio por bueno y con qué argumento.
 *
 * Por eso el cierre se abre en dos pasos y el campo no admite menos de tres
 * caracteres — la misma regla que valida el servidor.
 */

const MINIMO_NOTA = 3;

/**
 * Cuántos casos se piden.
 *
 * No es «los que haya»: una empresa con la configuración incompleta puede
 * acumular cientos, y traerlos todos para enseñar los que importan es pagar
 * una consulta grande por nada.
 *
 * El paginador camina por todos los que lleguen, así que el tope sólo se
 * alcanzaría en una empresa con más de doscientos casos ABIERTOS a la vez —y
 * ahí el problema no es este modal, es que nadie está cerrando nada—.
 */
const TOPE_CASOS = 200;

/** A partir de aquí la lista deja de recorrerse de un vistazo y ofrece filtro. */
const MUCHOS = 8;

/**
 * Casos por página.
 *
 * Cinco cabe entero en la ventana sin scroll en pantallas normales, y cada
 * renglón trae hecho, lugar, tiempo y resumen: son cuatro líneas de lectura.
 * Diez de eso no se recorren, se hojean — y hojear una lista de cosas que hay
 * que decidir es cómo se cierra la equivocada.
 *
 * El paginador no aparece con cinco o menos: con una sola página sería un
 * control que no hace nada.
 */
const POR_PAGINA = 5;

/*
 * La antigüedad del caso la formatea `lapso`.
 *
 * Vivía aquí como función propia que sólo sabía de minutos y horas, así que un
 * caso de un mes se anunciaba como «abierto hace 43 200 min». Ahora escala a
 * días y meses, y lo hace igual en este modal, en la ficha del caso y en la
 * pestaña de Casos del mapa de unidades.
 */

export interface CasosAbiertosModalProps {
  onClose: () => void;
  /**
   * Abrir con los de UNA unidad arriba y el resto debajo.
   *
   * Lo usa el botón de la tarjeta de unidad. No filtra: quien llegó desde una
   * unidad concreta casi siempre quiere ver ese caso, pero si de paso hay
   * otros tres esperando, ocultárselos no le hace ningún favor.
   */
  deviceIdPrioritario?: string | null;
  /**
   * Cómo se nombra cada caso.
   *
   * Opcional porque `labelOfCase` cuelga del hook del Centro de monitoreo, y
   * este modal se abre también desde Resumen y Unidades, que no lo usan.
   * Cuando no viene, se arma con el origen del caso —que es lo mismo que hace
   * ese hook cuando el caso no pertenece a una amenaza agrupada—.
   */
  labelOfCase?: (item: CentinelaCaseView) => string;
}

export default function CasosAbiertosModal({
  onClose,
  deviceIdPrioritario = null,
  labelOfCase,
}: CasosAbiertosModalProps) {
  const { t } = useTranslation("common");

  const nombrar = (item: CentinelaCaseView) =>
    labelOfCase
      ? labelOfCase(item)
      : t(`monitoreoIa.caseEvents.origin.${item.origin}`, { defaultValue: item.kind });
  const casesQuery = useCentinelaCases({ limit: TOPE_CASOS });
  const cerrar = useCerrarCaso();

  /** Qué caso tiene el formulario de cierre abierto. Uno a la vez, a propósito. */
  const [cerrando, setCerrando] = useState<string | null>(null);
  const [nota, setNota] = useState("");
  const [soloEsperan, setSoloEsperan] = useState(false);
  const [pagina, setPagina] = useState(0);

  const abiertos = useMemo(() => {
    const todos = mapCentinelaCases(casesQuery.data?.cases).filter((c) => c.isOpen);

    // Primero el de la unidad desde la que se abrió, después los que esperan a
    // una persona, y al final el resto por antigüedad. El mismo criterio que la
    // lista de inteligencia en vivo: manda a quién le toca actuar, no la hora.
    const peso = (c: CentinelaCaseView) => {
      if (deviceIdPrioritario && c.deviceId === deviceIdPrioritario) return 0;
      if (c.requiresHuman) return 1;
      return 2;
    };
    return [...todos].sort((a, b) => {
      const d = peso(a) - peso(b);
      if (d !== 0) return d;
      return String(b.openedAt || "").localeCompare(String(a.openedAt || ""));
    });
  }, [casesQuery.data, deviceIdPrioritario]);

  const esperan = abiertos.filter((c) => c.requiresHuman).length;

  /*
    El filtro esconde renglones, así que el conteo de arriba sigue contando
    TODOS: si dijera «3 abiertos» con el filtro puesto, alguien cerraría esos
    tres y se iría creyendo que vació la lista.
  */
  const visibles = soloEsperan ? abiertos.filter((c) => c.requiresHuman) : abiertos;

  const paginas = Math.max(1, Math.ceil(visibles.length / POR_PAGINA));

  /*
    La página se acota en el render y no con un efecto. La lista se encoge sola
    —al cerrar un caso desaparece— y si estabas en la última te quedabas
    mirando una página vacía con el paginador diciendo «3 de 2». Acotar aquí es
    una línea y no puede desincronizarse.
  */
  const paginaActual = Math.min(pagina, paginas - 1);
  const desde = paginaActual * POR_PAGINA;
  const enPagina = visibles.slice(desde, desde + POR_PAGINA);

  const irA = (n: number) => {
    setPagina(n);
    // Cambiar de página con un formulario de cierre abierto dejaría la nota
    // escrita para un caso que ya no se ve.
    setCerrando(null);
    setNota("");
  };

  const cerrarEste = (caseId: string) => {
    const texto = nota.trim();
    if (texto.length < MINIMO_NOTA) return;
    cerrar.mutate(
      { caseId, resolution: texto },
      {
        onSuccess: () => {
          setCerrando(null);
          setNota("");
        },
      },
    );
  };

  return (
    <Modal
      title={t("monitoreoIa.casosAbiertos.titulo", { defaultValue: "Casos abiertos" })}
      onClose={onClose}
      width="w-full max-w-2xl"
      content={
        /*
          ── Alto fijo, un solo scroll ────────────────────────────────────────
          El Modal base ya trae `max-h-[95vh] overflow-y-auto` y su contenido
          otro `overflow-y-auto`. Con la lista scrolleando también eran TRES
          contenedores anidados: con diez casos salía una barra dentro de otra
          y arrastrar la de en medio movía la de fuera.

          Ahora el encabezado y el pie quedan fijos y sólo la lista se mueve,
          con un alto acotado para que el contenedor de fuera nunca tenga que
          scrollear. La ventana mide lo mismo con dos casos que con cincuenta.
        */
        <div className="flex max-h-[70dvh] flex-col">
          {casesQuery.isLoading ? (
            <div className="flex items-center gap-2 py-6 text-[13px] text-content-faint">
              <Spinner size="sm" />
              {t("monitoreoIa.casosAbiertos.cargando", { defaultValue: "Buscando casos abiertos…" })}
            </div>
          ) : abiertos.length === 0 ? (
            /* Sin casos no se enseña una lista vacía con un encabezado: se dice
               que no hay nada pendiente, que es la noticia. */
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <i className="ri-shield-check-line text-2xl text-success" />
              <p className="text-[13px] font-semibold text-content">
                {t("monitoreoIa.casosAbiertos.vacio", { defaultValue: "No hay casos abiertos." })}
              </p>
              <p className="text-xs text-content-faint">
                {t("monitoreoIa.casosAbiertos.vacioPie", {
                  defaultValue: "Centinela sigue vigilando; si algo pasa, aparece aquí.",
                })}
              </p>
            </div>
          ) : (
            <>
              {/* ── Cuántos son, antes de la lista ─────────────────────────
                  Con diez renglones y scroll, «¿cuántos me faltan?» deja de
                  contestarse mirando. El conteo va arriba y NO se mueve. */}
              <div className="mb-2 flex shrink-0 flex-wrap items-center gap-2 text-[11px]">
                <span className="text-content-secondary">
                  {t("monitoreoIa.casosAbiertos.conteo", {
                    defaultValue: "{{count}} abiertos",
                    count: abiertos.length,
                  })}
                </span>
                {esperan > 0 ? (
                  <span className="font-semibold text-warning">
                    · {t("monitoreoIa.casosAbiertos.conteoEsperan", {
                      defaultValue: "{{count}} te esperan",
                      count: esperan,
                    })}
                  </span>
                ) : null}

                {/* El filtro sólo aparece cuando hace falta. Un interruptor
                    permanente sobre una lista de tres es ruido. */}
                {abiertos.length > MUCHOS && esperan > 0 ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSoloEsperan((v) => !v);
                      // Filtrar cambia cuántos hay: quedarse en la página 4 de
                      // una lista que ahora tiene dos es mirar un vacío.
                      irA(0);
                    }}
                    className={`ml-auto rounded-lg border px-2 py-1 text-[10.5px] max-sm:min-h-9 max-sm:py-1.5 max-sm:text-[11.5px] font-medium transition-colors ${
                      soloEsperan
                        ? "border-warning/40 bg-warning/10 text-warning"
                        : "border-line text-content-secondary hover:bg-subtle"
                    }`}
                  >
                    <i className="ri-filter-3-line mr-1" aria-hidden="true" />
                    {t("monitoreoIa.casosAbiertos.soloEsperan", {
                      defaultValue: "Sólo los que me esperan",
                    })}
                  </button>
                ) : null}
              </div>

              <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
              {enPagina.map((item) => {
                const esteCerrando = cerrando === item.id;
                const deLaUnidad = deviceIdPrioritario && item.deviceId === deviceIdPrioritario;

                return (
                  <li
                    key={item.id}
                    className={`rounded-xl border p-3 ${
                      deLaUnidad
                        ? "border-primary-500/40 bg-primary-500/5"
                        : item.requiresHuman
                          ? "border-warning/40 bg-warning/5"
                          : "border-line bg-surface"
                    }`}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={CASE_URGENCY_TONES[item.urgency]} size="sm">
                        {t(`monitoreoIa.caseEvents.urgency.${item.urgency}`)}
                      </Badge>
                      <span className="text-[12px] font-semibold text-content">
                        {item.unitName ?? t("monitoreoIa.units.noDriver")}
                      </span>
                      {item.requiresHuman ? (
                        <span className="font-mono text-[9px] max-sm:text-[10px] font-semibold uppercase tracking-wider text-warning">
                          <i className="ri-user-follow-line mr-0.5" />
                          {t("monitoreoIa.monitoringCenter.esperandoAlguien", { defaultValue: "Te toca" })}
                        </span>
                      ) : null}
                      {/* La hora de apertura se fue a la línea de abajo, junto
                          al lugar: ahí dice «abierto hace 1 h 23 min», que es
                          la misma información sin obligar a mirar dos esquinas
                          distintas del renglón. */}
                    </div>

                    {/* ── El hecho, con sus números ─────────────────────────
                        `hecho` es el `alert_label` que escribe Centinela al
                        abrir el caso —«Parada no autorizada. Lleva 15 minutos
                        detenida, después de venir en marcha»— y dice mucho más
                        que el nombre del tipo de alerta. Se prefiere a
                        `nombrar()`, que sólo traduce el origen y acaba diciendo
                        «Caso» en todos los renglones. */}
                    <p className="mt-1 text-[11.5px] font-medium text-content-secondary">
                      {item.hecho ?? nombrar(item)}
                    </p>

                    {/* ── Dónde y cuánto lleva ──────────────────────────────
                        Las dos preguntas que se hacen antes de decidir si
                        cerrar: dónde pasó y desde cuándo está así. Sin ellas
                        hay que abrir el caso para saberlo, y entonces el modal
                        no ahorró nada.

                        `donde` puede ser una geocerca, una calle o unas
                        coordenadas; cuando de verdad no se sabe, se dice —un
                        hueco se lee como si la línea no hubiera cargado. */}
                    <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[9.5px] text-content-faint">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-map-pin-line" aria-hidden="true" />
                        {item.donde ?? t("monitoreoIa.casosAbiertos.sinUbicacion", {
                          defaultValue: "sin ubicación",
                        })}
                      </span>
                      {item.duracionMin != null ? (
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-time-line" aria-hidden="true" />
                          {t("monitoreoIa.casosAbiertos.abiertoHace", {
                            defaultValue: "abierto hace {{tiempo}}",
                            tiempo: lapso(item.duracionMin, t),
                          })}
                        </span>
                      ) : null}
                    </p>

                    {/* El resumen de Centinela es lo que permite decidir sin
                        abrir nada más. Sin él, cerrar desde aquí sería firmar
                        a ciegas. */}
                    {item.summary ? (
                      <p className="mt-1 text-[11px] leading-snug text-content-faint">
                        {item.summary}
                      </p>
                    ) : null}

                    {esteCerrando ? (
                      <div className="mt-2 space-y-2">
                        <label className="block text-[10.5px] text-content-faint" htmlFor={`nota-${item.id}`}>
                          {t("monitoreoIa.cerrarCaso.porQue", {
                            defaultValue: "¿Por qué se da por resuelto?",
                          })}
                        </label>
                        <textarea
                          id={`nota-${item.id}`}
                          value={nota}
                          onChange={(e) => setNota(e.target.value)}
                          rows={2}
                          className="w-full rounded-lg border border-line bg-card p-2 text-[11.5px] text-content outline-none focus:border-primary-500"
                          placeholder={t("monitoreoIa.cerrarCaso.ejemplo", {
                            defaultValue: "Hablé con el operador: cargando en el patio.",
                          })}
                        />
                        {cerrar.isError ? (
                          <p className="text-[10.5px] text-error">
                            {t("monitoreoIa.cerrarCaso.fallo", {
                              defaultValue: "No se pudo cerrar. Intenta de nuevo.",
                            })}
                          </p>
                        ) : null}
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            disabled={nota.trim().length < MINIMO_NOTA || cerrar.isPending}
                            onClick={() => cerrarEste(item.id)}
                          >
                            {cerrar.isPending
                              ? t("monitoreoIa.cerrarCaso.cerrando", { defaultValue: "Cerrando…" })
                              : t("monitoreoIa.cerrarCaso.confirmar", { defaultValue: "Cerrar el caso" })}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setCerrando(null);
                              setNota("");
                            }}
                          >
                            {t("actions.cancel", { defaultValue: "Cancelar" })}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setCerrando(item.id);
                          setNota("");
                        }}
                        className="mt-2 flex items-center gap-1 rounded-lg border border-line px-2.5 py-1.5 text-[10.5px] font-medium text-content-secondary transition-colors hover:bg-subtle"
                      >
                        <i className="ri-check-double-line" aria-hidden="true" />
                        {t("monitoreoIa.casosAbiertos.resolver", { defaultValue: "Resolver" })}
                      </button>
                    )}
                  </li>
                );
              })}
              </ul>

              {/* ── El paginador ────────────────────────────────────────
                  Oculto mientras haya una sola página: un control que no hace
                  nada enseña a ignorar esa zona de la ventana.

                  Reemplazó al aviso de «hay más de los que caben»: con páginas
                  no hay nada que no quepa, se camina. */}
              {paginas > 1 ? (
                <div className="mt-2 flex shrink-0 items-center justify-between gap-2 border-t border-line pt-2">
                  <button
                    type="button"
                    onClick={() => irA(paginaActual - 1)}
                    disabled={paginaActual === 0}
                    className="flex items-center gap-1 rounded-lg border border-line px-2.5 py-1.5 text-[10.5px] font-medium text-content-secondary transition-colors hover:bg-subtle disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
                  >
                    <i className="ri-arrow-left-s-line" aria-hidden="true" />
                    {t("actions.previous", { defaultValue: "Anterior" })}
                  </button>

                  <span className="font-mono text-[10.5px] text-content-faint">
                    {t("monitoreoIa.casosAbiertos.pagina", {
                      defaultValue: "{{actual}} de {{total}}",
                      actual: paginaActual + 1,
                      total: paginas,
                    })}
                  </span>

                  <button
                    type="button"
                    onClick={() => irA(paginaActual + 1)}
                    disabled={paginaActual >= paginas - 1}
                    className="flex items-center gap-1 rounded-lg border border-line px-2.5 py-1.5 text-[10.5px] font-medium text-content-secondary transition-colors hover:bg-subtle disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
                  >
                    {t("actions.next", { defaultValue: "Siguiente" })}
                    <i className="ri-arrow-right-s-line" aria-hidden="true" />
                  </button>
                </div>
              ) : null}

            </>
          )}
        </div>
      }
    />
  );
}
